import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { isAuthError } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  resetAuthPassword,
  signIn,
  signOut,
  signUp,
  updateAuthUser,
  updateAuthUserProfile,
  type ResetPasswordProps,
  type SignUpProps,
} from "@/api/resources/auth/api";
import { authQueries, authQueryKeys } from "@/api/resources/auth/queries";
import { usersQueryKeys } from "@/api/resources/users/queries";
import { useNotifications } from "@/hooks/useNotifications";
import { useWhitelabelBrand } from "@/hooks/useWhitelabelBrand";
import { getAuthErrorMessage } from "@/utils/authErrorMessage";
import { clearLocalStorage } from "@/utils/localStorageCache";
import { clearPosthogUser, setPosthogUser, trackPosthogEvent } from "@/utils/posthog";
import { clearSentryUser, setSentryUser } from "@/utils/sentry";

export const useAuthSession = () => {
  return useQuery(authQueries.getAuthSession());
};

export const useAuthUser = () => {
  return useQuery(authQueries.getAuthUser());
};

export const useAuthUserProfile = (id?: string) => {
  const { data: session } = useAuthSession();
  return useQuery({
    ...authQueries.getAuthUserProfile(id ?? session?.user.id ?? ""),
    select: (profile) => {
      setSentryUser(profile);
      setPosthogUser(profile);
      return profile;
    },
  });
};

export const useAuthUserPermissions = (id?: string) => {
  const { data: session } = useAuthSession();
  return useQuery(authQueries.getAuthUserPermissions(id ?? session?.user.id ?? ""));
};

export const useSignIn = () => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signIn,
    onMutate: () => {
      // remove all potential left-over queries in react-query cache, to mitigate conflicts and trigger re-evaluation of ALL app data
      queryClient.removeQueries();
    },
    onError: (_error, _variables, _context) => notify.error(t("notify.login.error")),
    onSuccess: () => {
      notify.success(t("notify.login.success"));
      trackPosthogEvent(`signIn`, `user`);
    },
  });
};

export const useSignUp = () => {
  const { t, i18n } = useTranslation();
  const notify = useNotifications();
  const { whitelabelBrand, appBaseUrl } = useWhitelabelBrand();

  return useMutation({
    mutationFn: (values: Pick<SignUpProps, "email" | "password">) => {
      return signUp({
        ...values,
        language: i18n.resolvedLanguage,
        whitelabelBrandKey: whitelabelBrand.key,
        appBaseUrl,
      });
    },
    onError: () => notify.error(t("notify.register.error")),
    onSuccess: () => {
      notify.success(t("notify.register.success"));
      trackPosthogEvent(`signUp`, `user`);
    },
  });
};

export const useSignOut = () => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signOut,
    onMutate: async () => await queryClient.cancelQueries({ queryKey: MASTER_INVALIDATION_KEY }),
    onError: () => notify.error(t("notify.logout.error")),
    onSuccess: () => {
      // completely remove all queries in the react-query cache (doesn't trigger re-evaluation)
      queryClient.removeQueries();
      clearLocalStorage();
      trackPosthogEvent(`signOut`, `user`);
      clearSentryUser();
      clearPosthogUser();
      notify.success(t("notify.logout.success"));
    },
  });
};

export const useResetAuthPassword = () => {
  const { t, i18n } = useTranslation();
  const notify = useNotifications();
  const { whitelabelBrand, appBaseUrl } = useWhitelabelBrand();

  return useMutation({
    mutationFn: (values: Pick<ResetPasswordProps, "email">) => {
      return resetAuthPassword({
        ...values,
        language: i18n.resolvedLanguage,
        whitelabelBrandKey: whitelabelBrand.key,
        appBaseUrl,
      });
    },
    onError: () => notify.error(t("notify.forgot-password.error")),
    onSuccess: () => {
      notify.success(t("notify.forgot-password.success"));
      trackPosthogEvent(`resetPassword`, `user`);
    },
  });
};

export const useUpdateAuthUser = (id?: string) => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAuthUser,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.cancelQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
    },
    onError: (error) => {
      if (isAuthError(error)) {
        notify.error(getAuthErrorMessage(error));
      } else {
        notify.error(t("notify.update.error", { e: t("user") }));
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.invalidateQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
      notify.success(t("notify.update.success", { e: t("user") }));
      trackPosthogEvent(`update`, `user`);
    },
  });
};

export const useUpdateAuthUserProfile = (id?: string) => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAuthUserProfile,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.cancelQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
    },
    onError: () => notify.error(t("notify.update.error", { e: t("user") })),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.invalidateQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
      notify.success(t("notify.update.success", { e: t("user") }));
      trackPosthogEvent(`update`, `user`);
    },
  });
};

export const useAuth = () => {
  // we're using the sync mutate functions, since they don't need to be wrapped & handled via try-catch
  // otherwise try-catch would be mandatory and all error handling of react-query (see sources of used hooks) would be useless
  const { data: session, isLoading: loadingSession } = useAuthSession();
  const { data: user, isLoading: loadingUser } = useAuthUser();
  const { data: profile, isLoading: loadingProfile } = useAuthUserProfile();
  const { data: permissions, isLoading: loadingPermissions } = useAuthUserPermissions();

  const { mutate: signIn, isPending: loadingSignIn } = useSignIn();
  const { mutate: signUp, isPending: loadingSignUp } = useSignUp();
  const { mutate: signOut, isPending: loadingSignOut } = useSignOut();
  const { mutate: resetAuthPassword, isPending: loadingResetAuthPassword } = useResetAuthPassword();
  const { mutate: updateAuthUser, isPending: loadingUpdateAuthUser } = useUpdateAuthUser();
  const { mutate: updateAuthUserProfile, isPending: loadingUpdateAuthUserProfile } = useUpdateAuthUserProfile();

  const loading = useMemo(
    () =>
      [
        loadingSession,
        loadingUser,
        loadingProfile,
        loadingPermissions,
        loadingSignIn,
        loadingSignUp,
        loadingSignOut,
        loadingResetAuthPassword,
        loadingUpdateAuthUser,
        loadingUpdateAuthUserProfile,
      ].some((loadingState) => loadingState),
    [
      loadingSession,
      loadingUser,
      loadingProfile,
      loadingPermissions,
      loadingResetAuthPassword,
      loadingSignIn,
      loadingSignOut,
      loadingSignUp,
      loadingUpdateAuthUser,
      loadingUpdateAuthUserProfile,
    ],
  );

  return {
    loading: loading,
    session: session,
    user: user,
    profile: profile,
    permissions: permissions,
    signIn: signIn,
    signUp: signUp,
    signOut: signOut,
    resetAuthPassword: resetAuthPassword,
    updateAuthUser: updateAuthUser,
    updateAuthUserProfile: updateAuthUserProfile,
  };
};
